import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { site } from "../lib/site";
import { breadcrumbJsonLd } from "../lib/structured-data";

export function Confidentialite() {
  const updated = "18 avril 2026";
  return (
    <>
      <SEO
        title={`Politique de confidentialité - ${site.name}`}
        description="Politique de confidentialité d'Anexya : collecte, traitement et protection de vos données personnelles conformément au RGPD."
        path="/politique-de-confidentialite"
        jsonLd={breadcrumbJsonLd([
          { name: "Accueil", url: `${site.baseUrl}/` },
          {
            name: "Politique de confidentialité",
            url: `${site.baseUrl}/politique-de-confidentialite`,
          },
        ])}
      />
      <PageHeader
        eyebrow="Mentions RGPD"
        title="Politique de confidentialité"
        subtitle={`Dernière mise à jour : ${updated}`}
        breadcrumbs={[
          { label: "Accueil", to: "/" },
          { label: "Politique de confidentialité" },
        ]}
      />

      <section className="section bg-white">
        <div className="container-page max-w-3xl prose-legal">
          <p>
            La présente politique décrit la manière dont {site.legalName}{" "}
            («&nbsp;nous&nbsp;», «&nbsp;notre&nbsp;», «&nbsp;nos&nbsp;»), éditeur du site
            <a href={site.baseUrl}> {site.baseUrl}</a>, collecte et traite vos données
            personnelles, conformément au Règlement Général sur la Protection des Données
            (RGPD&nbsp;2016/679) et à la loi Informatique et Libertés modifiée.
          </p>

          <h2>1. Responsable du traitement</h2>
          <p>
            Le responsable du traitement est <strong>{site.founder}</strong>, Entrepreneur
            Individuel exerçant sous le nom commercial «&nbsp;{site.name}&nbsp;», dont le siège
            est situé à {site.address.postalCode} {site.address.city} (France),
            SIRET {site.siret}.
          </p>
          <p>
            Pour toute question relative à la protection de vos données, vous pouvez nous écrire
            à <a href={site.emailHref}>{site.email}</a> ou par courrier à l'adresse ci-dessus.
          </p>

          <h2>2. Données collectées</h2>
          <p>Nous collectons les données que vous nous communiquez volontairement :</p>
          <ul>
            <li>
              <strong>Via le formulaire de contact</strong>&nbsp;: email, téléphone, commune,
              type de demande, message.
            </li>
            <li>
              <strong>Lors d'un échange téléphonique ou par email</strong>&nbsp;: les informations
              nécessaires pour établir un devis.
            </li>
            <li>
              <strong>Données techniques</strong>&nbsp;: adresse IP, navigateur, pages consultées
              - uniquement à des fins de sécurité et de statistiques anonymisées.
            </li>
          </ul>

          <h2>3. Finalités & bases légales</h2>
          <ul>
            <li>
              Répondre à votre demande de contact ou de devis - <em>exécution de mesures
              précontractuelles (art. 6.1.b RGPD)</em>.
            </li>
            <li>
              Assurer le suivi de la relation client et exécuter le contrat - <em>exécution du
              contrat (art. 6.1.b)</em>.
            </li>
            <li>
              Respecter nos obligations comptables et légales - <em>obligation légale (art.
              6.1.c)</em>.
            </li>
            <li>
              Améliorer nos services et mesurer l'audience du site - <em>intérêt légitime (art.
              6.1.f)</em>, avec des cookies strictement nécessaires ou soumis à consentement.
            </li>
          </ul>

          <h2>4. Durées de conservation</h2>
          <ul>
            <li>Demandes de contact non contractualisées&nbsp;: 3 ans après le dernier échange.</li>
            <li>
              Dossiers clients&nbsp;: 10 ans après la fin du contrat, pour nos obligations
              comptables et fiscales.
            </li>
            <li>Logs techniques&nbsp;: 12 mois maximum.</li>
          </ul>

          <h2>5. Destinataires des données</h2>
          <p>
            Vos données sont exclusivement destinées à nos équipes habilitées. Elles peuvent être
            transmises à des sous-traitants techniques (hébergeur, messagerie, outil de devis,
            outil de mesure d'audience) tenus au même niveau de confidentialité. Aucun transfert
            hors UE n'est effectué sans garanties appropriées (clauses contractuelles types).
          </p>

          <h2>6. Cookies</h2>
          <p>
            Lors de votre première visite, un bandeau de consentement vous permet d'accepter,
            refuser ou personnaliser le dépôt de cookies. Deux catégories sont distinguées&nbsp;:
          </p>
          <ul>
            <li>
              <strong>Cookies strictement nécessaires</strong>&nbsp;: indispensables au
              fonctionnement du site et à la mémorisation de votre choix de consentement. Ils
              sont déposés sans consentement préalable, conformément à l'article 82 de la loi
              Informatique et Libertés.
            </li>
            <li>
              <strong>Cookies de mesure d'audience</strong>&nbsp;: nous utilisons{" "}
              <strong>Google Analytics 4</strong> (Google Ireland Limited) pour produire des
              statistiques anonymisées de visite, avec <em>Google Consent Mode v2</em>. Par
              défaut, tous les signaux sont refusés&nbsp;: aucun cookie{" "}
              <code>_ga</code> / <code>_ga_*</code> n'est déposé et seules des mesures
              anonymes et agrégées (sans identifiant, sans cookie) peuvent être transmises à
              des fins de modélisation statistique. Les cookies ne sont activés qu'après votre
              accord explicite. La configuration est durcie&nbsp;: IP anonymisée, signaux
              publicitaires et personnalisation désactivés, aucun partage avec Google Ads. En
              cas de retrait du consentement, les cookies déjà posés sont supprimés. Durée
              maximale&nbsp;: 13 mois.
            </li>
          </ul>
          <p>
            Vous pouvez modifier ou retirer votre consentement à tout moment via le lien
            «&nbsp;Gérer mes cookies&nbsp;» situé dans le pied de page. Aucun cookie publicitaire
            ou de profilage n'est utilisé.
          </p>

          <h2>7. Vos droits</h2>
          <p>
            Conformément au RGPD, vous disposez des droits d'accès, de rectification,
            d'effacement, de limitation, d'opposition et de portabilité sur vos données, ainsi
            que du droit de définir des directives post-mortem.
          </p>
          <p>
            Pour exercer ces droits, écrivez-nous à{" "}
            <a href={site.emailHref}>{site.email}</a> en justifiant de votre identité. Nous vous
            répondons dans un délai d'un mois.
          </p>
          <p>
            Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés,
            vous pouvez introduire une réclamation auprès de la CNIL - 3 place de Fontenoy, 75007
            Paris - <a href="https://www.cnil.fr">www.cnil.fr</a>.
          </p>

          <h2>8. Sécurité</h2>
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables pour
            préserver la confidentialité, l'intégrité et la disponibilité de vos données&nbsp;:
            chiffrement TLS, contrôle d'accès, sauvegardes régulières, sensibilisation des
            collaborateurs.
          </p>

          <h2>9. Mise à jour</h2>
          <p>
            La présente politique peut évoluer. Toute modification substantielle sera signalée
            sur cette page. Dernière mise à jour&nbsp;: {updated}.
          </p>
        </div>
      </section>
    </>
  );
}
